import { Fragment, useId } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import type { PaletteShortcut } from "./palette-shortcut";

/** What a screen reader says for a key printed as a symbol, which some read as nothing at all. */
const SPOKEN: Readonly<Record<string, string>> = {
  "⌘": "Command",
  "↑": "Up arrow",
  "↓": "Down arrow",
};

/** Every shortcut the app has, each key named as this platform's keyboard prints it. */
function rowsFor(shortcut: PaletteShortcut) {
  return [
    { keys: shortcut.keys, does: "Open or close the search" },
    { keys: ["↑", "↓"], does: "Move through the results" },
    { keys: [shortcut.enter], does: "Open the highlighted result" },
    { keys: [shortcut.back], does: "Go back a step, from an empty search box" },
    { keys: ["Esc"], does: "Close the search" },
  ];
}

function Keys({ keys }: { keys: readonly string[] }) {
  return keys.map((key, at) => (
    <Fragment key={key}>
      {at > 0 && " "}
      <kbd aria-label={SPOKEN[key]}>{key}</kbd>
    </Fragment>
  ));
}

export function KeyboardShortcutsDialog({
  shortcut,
  onClose,
}: {
  shortcut: PaletteShortcut;
  onClose: () => void;
}) {
  const sentenceId = useId();
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent aria-describedby={sentenceId}>
        <DialogTitle>Keyboard shortcuts</DialogTitle>
        <DialogDescription id={sentenceId}>
          The search opens from any page. Its other keys work once it is open.
        </DialogDescription>
        <table className="w-full text-[13px]">
          <tbody>
            {rowsFor(shortcut).map((row) => (
              <tr key={row.does} className="border-t border-border-subtle">
                <td className="py-2 pr-4 font-mono text-[12px] whitespace-nowrap text-primary-soft-foreground">
                  <Keys keys={row.keys} />
                </td>
                <td className="py-2 text-muted-foreground">{row.does}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
